import { GameSessionStatus } from '../../../assets/enums/game-session.enum.ts';
import { type ChallengeConfig } from '../../challenge-configs-api.ts';
import { type PerformanceStuckGroup, type PerformanceStuckSessions } from '../performance-api.ts';
import { centsToDollars, toCents } from './money.ts';
import { getGameName } from './sessions.ts';
import { type SessionsServiceSession } from './sessions-service-api.ts';

const OLDEST_LIMIT = 10;
const DAY_MS = 24 * 60 * 60 * 1000;
const STALE_AFTER_DAYS = 30;

const getAgeInDays = (session: SessionsServiceSession, now: Date) =>
  Math.floor((now.getTime() - Date.parse(session.created_at)) / DAY_MS);

const buildGroup = (sessions: SessionsServiceSession[], now: Date): PerformanceStuckGroup => ({
  count: sessions.length,
  older_than_30_days: sessions.filter((session) => getAgeInDays(session, now) >= STALE_AFTER_DAYS)
    .length,
  potential_payout: centsToDollars(
    sessions.reduce((sum, session) => sum + toCents(session.potential_payout), 0)
  ),
});

export const buildStuckSessions = (
  sessions: SessionsServiceSession[],
  configsById: Map<number, ChallengeConfig>,
  now: Date
): PerformanceStuckSessions => {
  const active = sessions.filter((session) => session.status === GameSessionStatus.Active);
  const awaitingPayment = sessions.filter(
    (session) => session.status === GameSessionStatus.AwaitingPayment
  );

  const oldest = [...active, ...awaitingPayment]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .slice(0, OLDEST_LIMIT)
    .map((session) => ({
      id: session.id,
      status: session.status,
      game_name: getGameName(session, configsById),
      created_at: session.created_at,
      age_days: getAgeInDays(session, now),
      potential_payout: centsToDollars(toCents(session.potential_payout)),
    }));

  return {
    active: buildGroup(active, now),
    awaiting_payment: buildGroup(awaitingPayment, now),
    oldest,
  };
};
